import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import {
  addSection,
  updateSection,
  deleteSection,
  addSectionCategory,
  deleteSectionCategory,
} from './section.api';
import { sectionKeys } from './section.queryKeys';
import { SectionEntry, SectionCategoryEntry } from '@/types/section.types';

export const useAddSectionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (section: SectionEntry) => addSection(section),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: sectionKeys.lists() });
      if (data.action_status) {
        toast.success(data.action_message || 'Η ενότητα δημιουργήθηκε επιτυχώς');
      } else {
        toast.error(data.action_message || 'Αποτυχία δημιουργίας ενότητας');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Αποτυχία δημιουργίας ενότητας');
    },
  });
};

export const useUpdateSectionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sectionId, section }: { sectionId: string; section: SectionEntry }) =>
      updateSection(sectionId, section),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: sectionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: sectionKeys.detail(variables.sectionId) });
      if (data.action_status) {
        toast.success(data.action_message || 'Η ενότητα ενημερώθηκε επιτυχώς');
      } else {
        toast.error(data.action_message || 'Αποτυχία ενημέρωσης ενότητας');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Αποτυχία ενημέρωσης ενότητας');
    },
  });
};

export const useDeleteSectionMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sectionId: string) => deleteSection(sectionId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: sectionKeys.lists() });
      if (data.action_status) {
        toast.success(data.action_message || 'Η ενότητα διαγράφηκε επιτυχώς');
      } else {
        toast.error(data.action_message || 'Αποτυχία διαγραφής ενότητας');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Αποτυχία διαγραφής ενότητας');
    },
  });
};

export const useAddSectionCategoryMutation = (sectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (category: SectionCategoryEntry) => addSectionCategory(sectionId, category),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: sectionKeys.categories(sectionId) });
      if (data.action_status) {
        toast.success(data.action_message || 'Η κατηγορία προστέθηκε επιτυχώς στην ενότητα');
      } else {
        toast.error(data.action_message || 'Αποτυχία προσθήκης κατηγορίας');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Αποτυχία προσθήκης κατηγορίας');
    },
  });
};

export const useDeleteSectionCategoryMutation = (sectionId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (sectionCategoryId: string) => deleteSectionCategory(sectionId, sectionCategoryId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: sectionKeys.categories(sectionId) });
      if (data.action_status) {
        toast.success(data.action_message || 'Η κατηγορία αφαιρέθηκε επιτυχώς από την ενότητα');
      } else {
        toast.error(data.action_message || 'Αποτυχία αφαίρεσης κατηγορίας');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Αποτυχία αφαίρεσης κατηγορίας');
    },
  });
};
